// ------------------------------------------------------------------------------
// name: types
// author: mudas
// created: 2024-10-15
// ------------------------------------------------------------------------------

import { RcFile } from 'antd/es/upload/interface';
import { FileACL } from 'common/types';
import UploadError from './UploadError';

// 数据存储限制了每个分片不能小于 5MB
export const SHUYUAN_CHUNK_LIMIT = 5 * 1024 * 1024;

// 分片上传配置数据
export const ChunkUploadConfig = {
  // 默认线程数量：1~20 之间的整数，代表同时开始上传的任务线程数，原则上越多越快，但会给服务器带来压力
  thread: 1,

  // TODO: 由于最小切片被限定在 5MB，那以切片为单位的进度就会显得太慢，出现卡顿或者跳跨度较大的进度
  //        因此需要使用 onUploadProgress 来细化每个切片内的上传进度反馈给上传组件
  // 最小切片尺寸，默认：5MB
  minChunkSize: SHUYUAN_CHUNK_LIMIT,

  // 最大切片大小，默认：8MB
  maxChunkSize: 8 * 1024 * 1024,

  // 进行分片上传的文件大小临界点（小于该大小将使用整个文件上传的方式进行上传，否则进行分片上传）
  // 设置成 0 则任何文件都启用分片上传
  chunkThreshold: SHUYUAN_CHUNK_LIMIT * 4,
  aliyunChunkThreshold: SHUYUAN_CHUNK_LIMIT * 20,
};

// 请求分片上传返回结果类型
export enum ChunkStatus {
  // 未完成初始化，没有可用的 chunk id
  NotReady = 'not_ready',

  // 上传的目标 url 获取失败
  GetUrlFailed = 'get_url_failed',

  // 正在上传分片
  Uploading = 'uploading',

  // 已暂停（重新开始上传，会上传所有未完成的分片）
  Paused = 'paused',

  // 上传遇到错误已终止
  Error = 'error',

  // 上传切片完成，但聚合失败
  ConcatError = 'concat_error',

  // 上传切片完成
  Completed = 'completed',
}

export interface ChunkUploadResult {
  e_tag: string;
  part_number: number;
}

export interface ChunkUploadParams {
  fileName: string;
  fileSize: string;
  contentType: string;

  // 权限类型：（public、 private、 group）当入参为 public 时，会返回文件的永久下载链接
  acl: FileACL;

  // 版本 uuid：acl为 group 类型时必传
  // 通过 /api/v2/version/getVersionUUID 接口获取 uuid
  // 因此 acl 为 group 时需要增加前置 uuid 流程，后置需要增加质检服务（/api/v2/file/uploadEnd）流程
  uuid?: string;

  // 存储目录：如	“/a/b”
  path?: string;
  namespace?: string; // 存储阿里云的命名空间
  overseas?: boolean; // 是否用海外加速
}

export interface BaseUploadId {
  fileId: string; // 'f094cc38-aca7-493c-acca-340d842effec';
  downloadUrl: string;
  url?: string;
}

// 获取分片上传的索引ID（分片id）
// /api/v2/file/getChunkUploadId 结果
export interface ChunkUploadId extends BaseUploadId {
  chunkUploadId: string; // '2~-B9KHKo7P3pq2puAJJiJggTv-Y9pnW4';
}

// 获取上传-预签名链接(单文件)
// /api/v2/file/getPreSignedUploadUrl 结果
export interface SingleUploadUrl extends BaseUploadId {
  preSignedUploadUrl: string; // 'https://fap-fos-dev.apps-sl.danlu.netease.com/pre-signed/upload/99999...';
}
export interface AliyunSingleUploadUrl extends BaseUploadId {
  uploadUrl: string;
  headers: Record<string, string>;
}

export interface ChunkOption {
  params?: Partial<ChunkUploadParams>;

  /**
   * 文件上传完成
   * @param data 上传完成后返回的 data（当前场景由预签名接口前置提供）
   * @param file
   */
  onFinished?: (data: BaseUploadId, file: RcFile) => void;

  // 文件上传失败
  onFailed?: (error: UploadError) => void;

  // 上传进度
  onProgress?: (percent: number, file: RcFile) => void;

  // ----------------------------------------
  // 针对分片上传的事件（单文件上传无效）
  // ----------------------------------------

  // 切片事件
  onChunkInit?: (index: number) => void; // 开始上传
  onChunkFailed?: (index: number) => void; // 切片上传失败
  onChunkCompleted?: (index: number) => void; // 切片上传完成

  // ----------------------------------------
  // 允许针对每次上传单文件时动态调整分片参数
  // ----------------------------------------

  // 1~20 之间的整数，代表同时开始上传的任务线程数，原则上越多越快，但会给服务器带来压力。
  thread?: number;

  // 分处上传的大小（默认：auto）
  // auto - 智能模式，取文件大小的 10% 做为分片大小，且最小不小于 512KB，最大不超过 2MB
  chunkSize?: number | 'auto';
}

export abstract class Uploader {
  abstract upload(file: RcFile, options?: ChunkOption);
}
